/**
 * Contatos do imóvel — quem MORA/usa, além de quem paga (M-TRA-7).
 *
 * `ClienteFinal` existia inteiro no backend — modelo, ViewSet com todas
 * as actions, dois serializers e TRÊS serviços de LGPD (desativar,
 * reativar, anonimizar) — e nenhuma tela o alcançava. Matá-lo fecharia a
 * porta de uma obrigação legal: o titular tem direito a pedir a exclusão
 * dos dados dele, e quem atende o pedido é a marcenaria, não o suporte.
 *
 * Por que aqui e não num menu próprio: o morador não é uma entidade
 * autônoma do sistema. Ele é do cliente — o arquiteto contrata, a família
 * mora. É onde a pessoa já está olhando quando precisa dessa informação.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiPlus, FiShield, FiUserCheck, FiUsers } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";

const PREFIXO = "/api/comercial/clientes-finais";

const PAPEIS = [
  ["MORADOR", "Mora no imóvel"],
  ["PROPRIETARIO", "Proprietário"],
  ["RESPONSAVEL", "Responsável pela obra"],
  ["OUTRO", "Outro"],
];

const VAZIO = {
  tipo: "PF",
  nome: "",
  documento: "",
  email: "",
  telefone: "",
  papel: "MORADOR",
  parentesco: "",
  assinatura_autorizada: false,
  observacoes: "",
};

export default function ContatosDoImovelSection({ clienteId, somenteLeitura = false }) {
  const { request, hasPermission, isMaster } = useAuth();

  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [editando, setEditando] = useState(null);
  const [dados, setDados] = useState(VAZIO);

  // Cliente anonimizado (CRM-10): a lista continua visível para consulta, mas
  // nenhum contato novo entra no cadastro que a LGPD mandou apagar.
  const podeMexer = !somenteLeitura;
  // Sem `cliente_final:view` o backend recusa a lista: o bloco some em vez
  // de aparecer vazio (vazio diz "não há contatos", o que não é verdade).
  const podeVer = isMaster || hasPermission("cliente_final:view");
  const podeEditar = podeMexer && (isMaster || hasPermission("cliente_final:edit"));
  const podeCriar = podeMexer && (isMaster || hasPermission("cliente_final:create"));
  const podeAnonimizar = podeMexer && (isMaster || hasPermission("lgpd:anonimizar"));

  const carregar = useCallback(async () => {
    if (!podeVer) return;
    setCarregando(true);
    try {
      const p = await request(
        `${PREFIXO}?cliente=${clienteId}&incluir_inativos=true`,
        { silentError: true },
      );
      setItens(Array.isArray(p) ? p : p?.results || []);
    } catch (_) {
      setItens([]);
    } finally {
      setCarregando(false);
    }
  }, [request, clienteId, podeVer]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrir = (item) => {
    setEditando(item || "novo");
    setDados(item ? { ...VAZIO, ...item } : VAZIO);
  };

  const salvar = async () => {
    if (!dados.nome.trim()) throw new Error("Informe o nome.");
    const corpo = { ...dados, cliente: clienteId };
    if (editando === "novo") {
      await request(PREFIXO, { method: "POST", body: JSON.stringify(corpo) });
    } else {
      await request(`${PREFIXO}/${editando.id}`, {
        method: "PATCH",
        body: JSON.stringify(corpo),
      });
    }
    setEditando(null);
    await carregar();
  };

  const acao = async (item, rota, confirmacao) => {
    if (confirmacao && !window.confirm(confirmacao)) return;
    await request(`${PREFIXO}/${item.id}/${rota}`, { method: "POST" });
    await carregar();
  };

  if (!podeVer) return null;

  return (
    <div className="cc" style={{ marginTop: 14 }}>
      <div className="cc__head">
        <div>
          <div className="cc__title">
            <FiUsers size={14} aria-hidden="true" /> Contatos do imóvel
          </div>
          <div className="cc__sub">
            Quem mora ou responde pela obra — nem sempre é quem paga
          </div>
        </div>
        {podeCriar && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => abrir(null)}>
            <FiPlus size={13} aria-hidden="true" /> Adicionar
          </button>
        )}
      </div>

      <div className="cc__body">
        {carregando ? (
          <div className="muted sm">Carregando…</div>
        ) : itens.length === 0 ? (
          <div className="muted sm">
            Nenhum contato cadastrado. Útil quando quem contrata não é quem
            recebe a entrega.
          </div>
        ) : (
          <table className="dtable">
            <colgroup>
              <col style={{ width: "34%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "24%" }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Papel</th>
                <th>Contato</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {itens.map((c) => (
                <tr key={c.id} style={c.ativo === false ? { opacity: 0.55 } : undefined}>
                  <td>
                    <strong>{c.nome}</strong>
                    {c.esta_anonimizado ? (
                      <span className="tag" style={{ marginLeft: 6 }}>anonimizado</span>
                    ) : null}
                    {c.ativo === false ? (
                      <span className="tag" style={{ marginLeft: 6 }}>inativo</span>
                    ) : null}
                    {c.parentesco ? (
                      <div className="sub">{c.parentesco}</div>
                    ) : null}
                  </td>
                  <td>
                    {c.papel_display || c.papel}
                    {c.assinatura_autorizada ? (
                      <div className="sub">
                        <FiUserCheck size={11} aria-hidden="true" /> pode assinar entrega
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {c.telefone || "—"}
                    {c.email ? <div className="sub ell">{c.email}</div> : null}
                  </td>
                  <td>
                    <div className="flex gap-2" style={{ justifyContent: "flex-end" }}>
                      {podeEditar && !c.esta_anonimizado && (
                        <button type="button" className="btn btn-ghost btn-sm"
                                onClick={() => abrir(c)}>Editar</button>
                      )}
                      {podeEditar && c.ativo !== false && (
                        <AsyncButton variant="secondary" silentError
                          successMessage="Contato desativado."
                          style={{ padding: "4px 8px", fontSize: 12 }}
                          onClick={() => acao(c, "desativar")}>
                          Desativar
                        </AsyncButton>
                      )}
                      {podeEditar && c.ativo === false && !c.esta_anonimizado && (
                        <AsyncButton variant="secondary" silentError
                          successMessage="Contato reativado."
                          style={{ padding: "4px 8px", fontSize: 12 }}
                          onClick={() => acao(c, "reativar")}>
                          Reativar
                        </AsyncButton>
                      )}
                      {/* LGPD: o titular tem direito a pedir a exclusão dos
                          dados dele. É irreversível — o registro fica (as
                          FKs de entrega/assinatura dependem dele), os dados
                          pessoais somem. */}
                      {podeAnonimizar && !c.esta_anonimizado && (
                        <AsyncButton variant="danger" silentError
                          successMessage="Dados pessoais anonimizados."
                          style={{ padding: "4px 8px", fontSize: 12 }}
                          onClick={() => acao(
                            c, "anonimizar",
                            `Anonimizar os dados de ${c.nome}?\n\n`
                            + "Isto atende ao direito ao esquecimento (LGPD) e é "
                            + "IRREVERSÍVEL: nome, documento, e-mail e telefone "
                            + "são apagados. O registro permanece para não quebrar "
                            + "o histórico de entregas e assinaturas.",
                          )}>
                          <FiShield size={12} aria-hidden="true" /> Anonimizar
                        </AsyncButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SlideOverPanel
        open={editando != null}
        onClose={() => setEditando(null)}
        title={editando === "novo" ? "Novo contato do imóvel" : "Editar contato"}
        width="min(95vw, 520px)"
        footer={
          <div className="flex gap-2" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn btn-ghost"
                    onClick={() => setEditando(null)}>Cancelar</button>
            <AsyncButton variant="primary" onClick={salvar}
                         successMessage="Contato salvo."
                         errorMessage="Não foi possível salvar.">
              Salvar
            </AsyncButton>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Nome *</span>
            <input className="input" value={dados.nome}
                   onChange={(e) => setDados((d) => ({ ...d, nome: e.target.value }))} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Papel</span>
            <select className="input" value={dados.papel}
                    onChange={(e) => setDados((d) => ({ ...d, papel: e.target.value }))}>
              {PAPEIS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Telefone</span>
            <input className="input" value={dados.telefone}
                   onChange={(e) => setDados((d) => ({ ...d, telefone: e.target.value }))} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>E-mail</span>
            <input className="input" value={dados.email}
                   onChange={(e) => setDados((d) => ({ ...d, email: e.target.value }))} />
          </label>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={!!dados.assinatura_autorizada}
                   onChange={(e) => setDados(
                     (d) => ({ ...d, assinatura_autorizada: e.target.checked }),
                   )} />
            <span>
              Pode assinar o recebimento da entrega
              {/* CRM-38: o que a opção faz de verdade. O link público não
                  comprova identidade, então ela orienta e alerta — nunca
                  impede a entrega. */}
              <span style={{ display: "block", fontSize: 11, opacity: 0.7 }}>
                Quem estará no imóvel no dia. O nome aparece na página de
                assinatura e, se quem assinar não estiver na lista, a logística
                é avisada para conferir.
              </span>
            </span>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Observações</span>
            <textarea className="input" rows={3} value={dados.observacoes}
                      onChange={(e) => setDados(
                        (d) => ({ ...d, observacoes: e.target.value }),
                      )} />
          </label>
        </div>
      </SlideOverPanel>
    </div>
  );
}
